import { apiClient } from './client';
import type { Recipe, RecipeListItem, RecipeRating, SearchResult } from '@/types';

interface RecipesResponse {
  recipes: RecipeListItem[];
}

interface RecipeResponse {
  recipe: Recipe;
}

interface RatingResponse {
  rating: RecipeRating | null;
}

interface SearchResponse {
  recipes: SearchResult[];
}

interface CreateRecipeData {
  name: string;
  author: string;
  ingredients: string[];
  instructions: string[];
  image?: string;
  source_data?: {
    type: 'youtube' | 'cookbook' | 'manual' | 'web';
    youtube_url?: string;
    book_name?: string;
    amazon_link?: string;
  };
  forked_from?: {
    recipe_id: string;
    recipe_name: string;
    author: string;
  };
}

// Public endpoints
export async function getPublicRecipes(): Promise<RecipeListItem[]> {
  const data = await apiClient.get<RecipesResponse>('/public/recipes');
  return data.recipes;
}

export async function searchRecipes(query: string, limit = 20): Promise<SearchResult[]> {
  const data = await apiClient.get<SearchResponse>(
    `/public/search?q=${encodeURIComponent(query)}&limit=${limit}`
  );
  return data.recipes || [];
}

export async function getRecipe(id: string): Promise<Recipe> {
  const data = await apiClient.get<RecipeResponse>(`/recipes/${id}`);
  return data.recipe;
}

// Authenticated endpoints
export async function getUserRecipes(): Promise<RecipeListItem[]> {
  const data = await apiClient.get<RecipesResponse>('/recipes');
  return data.recipes;
}

export async function createRecipe(recipe: CreateRecipeData): Promise<Recipe> {
  const data = await apiClient.post<RecipeResponse>('/recipes', recipe);
  return data.recipe;
}

export async function forkRecipe(recipeId: string): Promise<Recipe> {
  const data = await apiClient.post<RecipeResponse>(`/recipes/${recipeId}/fork`);
  return data.recipe;
}

// Rating endpoints
export async function getRecipeRating(recipeId: string): Promise<RecipeRating | null> {
  const data = await apiClient.get<RatingResponse>(`/recipes/${recipeId}/rating`);
  return data.rating;
}

export async function submitRating(
  recipeId: string,
  rating: number,
  review?: string
): Promise<void> {
  await apiClient.post(`/recipes/${recipeId}/rating`, { rating, review });
}

// Random recipes for landing page
export async function getRandomRecipes(count = 6): Promise<RecipeListItem[]> {
  const data = await apiClient.get<RecipesResponse>(`/public/recipes/random?count=${count}`);
  return data.recipes;
}
